<script lang="ts">
export default defineComponent({
  inheritAttrs: false,
});
</script>
<script lang="ts" setup>
import merge from "deepmerge";
import { isPlainObject } from "is-plain-object";
import {
  DefineComponent,
  computed,
  defineComponent,
  getCurrentInstance,
  h,
  markRaw,
  onErrorCaptured,
  onMounted,
  provide,
  ref,
  useAttrs,
  useSlots,
  watch,
} from "vue";
import { dispatch, listen } from "../../../utils/bus";
import { loadComponent, loadComponentData } from "../../../utils/component";
import { showDialog } from "../../../utils/dialog";
import { buildMedicusUrl } from "../../../utils/route";
import ErrorBoundary from "../../ErrorBoundary.vue";
import ErrorComponent from "../../ErrorComponent";

const props = defineProps({
  url: {
    type: String,
    required: true,
  },

  componentPath: String,

  noData: Boolean,

  refreshEvent: {
    type: [String, Array],
    default: (props: any) => (props.noData ? "" : "reloadData"),
  },

  noRefresh: Boolean,

  disable: Boolean,
  disabled: Boolean,

  reload: Boolean,
});

defineEmits({
  rendered: () => true,
  "dynamic:mounted": () => true,
});
const attrs = useAttrs();
const slots = useSlots();

const errorMessage = ref("");

const vueInstance = getCurrentInstance();

onErrorCaptured((e) => {
  // eslint-disable-next-line no-undef
  if (IS_APP && import.meta.env.NODE_ENV === "development") {
    showDialog({
      title: e.message,
      component: ErrorComponent,
      componentProps: {
        error: e,
      },
    });
  } else {
    // errorCaptured.value = e;
  }

  return false;
});

const notFound = h("div", "Not found");

function resolveComponent(url: string) {
  return markRaw(
    defineComponent({
      async setup() {
        errorMessage.value = "";
        // await promisedTimeout(10000);
        let comp: any = notFound;
        try {
          const appContext = getCurrentInstance()!.appContext;

          comp = await loadComponent(
            url,
            props.noData,
            props.componentPath,
            undefined,
            undefined,
            appContext.config.globalProperties,
          );
        } catch (e) {
          // ignore
        }
        if (!comp) {
          comp = comp = defineComponent(
            () => () => h("div", { class: "error-text" }, "Error loading component"),
          );
        }
        return () =>
          h(
            comp,
            {
              ...attrs,
              ref: "comp",
            },
            slots,
          );
      },
    }),
  );
}

const render = ref(resolveComponent(props.url));
const comp = computed(() => rendered.value?.$refs.comp as InstanceType<DefineComponent>);

const rendered = ref<InstanceType<ReturnType<typeof resolveComponent>> | null>(null);
async function refreshData() {
  if (props.noData) return;
  if (comp.value) {
    try {
      const data: any = await loadComponentData(props.url, props.componentPath);
      Object.assign(
        comp.value.$data,
        merge(comp.value.$data, data, {
          isMergeableObject: isPlainObject,
        }),
      );
    } catch {
      // ignore
    }
  }
}

watch(
  () => [props.url] as const,
  ([x], prev) => {
    if (prev) {
      const [purl] = prev;

      const prevMedicus = buildMedicusUrl(purl);
      const currMedicus = buildMedicusUrl(x);

      if (prevMedicus.componentPath === currMedicus.componentPath && !props.reload) {
        if (prevMedicus.data !== currMedicus.data) {
          refreshData();
        }
      } else {
        render.value = resolveComponent(x);
      }
    }
  },
);

onMounted(() => {
  if (!props.noRefresh && props.refreshEvent) {
    const events = Array.isArray(props.refreshEvent)
      ? (props.refreshEvent as string[])
      : props.refreshEvent.split(",").map((x) => x.trim());
    events.forEach((x) => {
      listen(x, refreshData);
    });
  }
});

watch(comp, (x) => {
  dispatch("dynamic:mounted", true);
  vueInstance?.emit("dynamic:mounted");
});

dispatch("dynamic:init", true);
provide("isDynamic", true);
provide(
  "isLoaded",
  computed(() => !!comp.value),
);

defineExpose({
  comp,

  refreshData,
});
</script>

<template>
  <error-boundary>
    <suspense>
      <div v-if="errorMessage" class="flex flex-auto items-center justify-center h-full">
        <span class="empty-text">{{ errorMessage }}</span>
      </div>
      <render v-else ref="rendered" />
      <template #fallback>
        <div class="flex flex-auto items-center justify-center h-full">
          <slot name="loading">
            <span class="empty-text">Loading...</span>
          </slot>
        </div>
      </template>
    </suspense>
  </error-boundary>
</template>
